import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info, Maximize2, X } from 'lucide-react';

const KB_BINDINGS = [
  { keys: ['W', '↑'], action: 'Forward'      },
  { keys: ['S', '↓'], action: 'Backward'     },
  { keys: ['A'],      action: 'Strafe left'  },
  { keys: ['D'],      action: 'Strafe right' },
  { keys: ['Q', '←'], action: 'Rotate CCW'   },
  { keys: ['E', '→'], action: 'Rotate CW'    },
];


const MAX_LIN = 0.27;
const MAX_YAW = 0.45;

const COMPACT_SIZE = 130;
const COMPACT_R = COMPACT_SIZE / 2;
const COMPACT_KNOB = 13;
const COMPACT_MAX_R = COMPACT_R - COMPACT_KNOB - 3;

function drawJoy(canvas: HTMLCanvasElement, kx: number, ky: number, dragging: boolean) {
  const size = canvas.width;
  const r = size / 2;
  const knob = Math.max(10, Math.round(r * 0.17));
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);

  // Outer border circle — bright blue
  ctx.beginPath(); ctx.arc(r, r, r - 2, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(96,165,250,0.95)'; ctx.lineWidth = 2; ctx.stroke();

  // Inner 3/5-radius reference circle — matches crosshair color
  ctx.beginPath(); ctx.arc(r, r, r * 0.6, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(147,197,253,0.55)'; ctx.lineWidth = 1.25; ctx.stroke();

  // Crosshair — more noticeable
  ctx.beginPath(); ctx.moveTo(r, 4); ctx.lineTo(r, size - 4); ctx.moveTo(4, r); ctx.lineTo(size - 4, r);
  ctx.strokeStyle = 'rgba(147,197,253,0.55)'; ctx.lineWidth = 1.25; ctx.stroke();

  // Knob — same bright blue as border, with slight glow
  ctx.beginPath(); ctx.arc(r + kx, r + ky, knob, 0, Math.PI * 2);
  ctx.fillStyle = '#60a5fa';
  ctx.shadowColor = '#60a5fa'; ctx.shadowBlur = dragging ? 18 : 10;
  ctx.fill(); ctx.shadowBlur = 0;
}

function clampJoy(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
  const r = canvas.width / 2;
  const knob = Math.max(10, Math.round(r * 0.17));
  const maxR = r - knob - 3;
  const rect = canvas.getBoundingClientRect();
  let dx = clientX - rect.left - r, dy = clientY - rect.top - r;
  const d = Math.sqrt(dx * dx + dy * dy);
  // Pointer is outside the visual circle — caller should treat as release.
  const outside = d > r;
  if (d > maxR) { dx *= maxR / d; dy *= maxR / d; }
  return { dx, dy, maxR, outside };
}

export interface JogPanelProps {
  baseOwner?: string | null;
  /** Identity that currently holds teleop (read from flow_base_service heartbeat). */
  teleopUser?: string | null;
  /** Identity of the viewer (their email / "local" / "Verne Engineer"). */
  currentUser?: string;
  activeWaypoint?: boolean;
  onJog: (vx: number, vy: number, vw: number) => void;
  onToggleTeleop: (currentlyOverridden: boolean) => void;
  renderCamera?: () => ReactNode;
  /** 'inline' (default): compact panel with optional fullscreen toggle.
   *  'mobile': single-purpose fullscreen layout — camera fills the background,
   *  joystick bottom-left, yaw slider bottom-right. */
  variant?: 'inline' | 'mobile';
  /** mobile only: X button calls this. */
  onClose?: () => void;
}

const GLOW_STYLE = `
  @keyframes jog-orange-glow {
    0%, 100% { box-shadow: 0 0 0px 0px rgba(251,146,60,0); }
    50%       { box-shadow: 0 0 8px 3px rgba(251,146,60,0.55); }
  }
  .jog-orange-glow { animation: jog-orange-glow 1s ease-in-out infinite; }
  .jog-mobile-slider {
    flex: 1;
    min-width: 0;
    height: 28px;
    touch-action: none;
    accent-color: #60a5fa;
  }
`;

const MOBILE_JOY_SIZE = 150;

export function JogPanel({ baseOwner, teleopUser, currentUser, activeWaypoint, onJog, onToggleTeleop, renderCamera, variant = 'inline', onClose }: JogPanelProps) {
  const velRef = useRef({ vx: 0, vy: 0, yaw: 0 });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Locked-out: another user holds teleop. Hide controls; only that user can release.
  const otherUserControlling = baseOwner === 'teleop'
    && teleopUser != null
    && currentUser != null
    && teleopUser !== currentUser;

  const overridden = baseOwner === 'teleop' && !otherUserControlling;
  const locked = !overridden;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const onJogRef = useRef(onJog);
  onJogRef.current = onJog;

  const [fsOpen, setFsOpen] = useState(false);
  const [showKb, setShowKb] = useState(false);
  const [compactYawValue, setCompactYawValue] = useState(0);
  const [fsYawValue, setFsYawValue] = useState(0);
  const [mobileYawValue, setMobileYawValue] = useState(0);
  const nativeFsActive = useRef(false);

  const compactSliderRef = useRef<HTMLInputElement>(null);
  const fsSliderRef = useRef<HTMLInputElement>(null);
  const mobileSliderRef = useRef<HTMLInputElement>(null);

  const startSend = useCallback(() => {
    if (timerRef.current) return;
    const fire = () => { onJogRef.current(velRef.current.vx, velRef.current.vy, -velRef.current.yaw * MAX_YAW); };
    fire(); timerRef.current = setInterval(fire, 40);
  }, []);

  const stopSend = useCallback(() => {
    const wasSending = !!timerRef.current;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    velRef.current = { vx: 0, vy: 0, yaw: 0 };
    if (wasSending) onJogRef.current(0, 0, 0);
    setCompactYawValue(0);
    setFsYawValue(0);
    setMobileYawValue(0);
    if (compactSliderRef.current) compactSliderRef.current.value = '0';
    if (fsSliderRef.current) fsSliderRef.current.value = '0';
    if (mobileSliderRef.current) mobileSliderRef.current.value = '0';
  }, []);

  const resetYaw = useCallback(() => {
    velRef.current.yaw = 0;
    setCompactYawValue(0);
    setFsYawValue(0);
    setMobileYawValue(0);
    if (compactSliderRef.current) compactSliderRef.current.value = '0';
    if (fsSliderRef.current) fsSliderRef.current.value = '0';
    if (mobileSliderRef.current) mobileSliderRef.current.value = '0';
  }, []);

  const resetYawAfterNativeInput = useCallback(() => {
    resetYaw();
    requestAnimationFrame(resetYaw);
  }, [resetYaw]);

  useEffect(() => { if (locked) stopSend(); }, [locked, stopSend]);

  // ── keyboard control ──────────────────────────────────────────────────────
  const keysHeld = useRef<Set<string>>(new Set());

  useEffect(() => {
    const KEYS = new Set(['w','a','s','d','q','e','arrowup','arrowdown','arrowleft','arrowright']);

    const applyKeys = () => {
      const k = keysHeld.current;
      velRef.current.vx  = (k.has('w') || k.has('arrowup'))    ?  MAX_LIN
                         : (k.has('s') || k.has('arrowdown'))   ? -MAX_LIN : 0;
      velRef.current.vy  = k.has('a') ?  MAX_LIN : k.has('d') ? -MAX_LIN : 0;
      velRef.current.yaw = (k.has('q') || k.has('arrowleft'))  ? -1
                         : (k.has('e') || k.has('arrowright'))  ?  1 : 0;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const key = e.key.toLowerCase();
      if (!KEYS.has(key) || lockedRef.current) return;
      e.preventDefault();
      if (keysHeld.current.has(key)) return;
      keysHeld.current.add(key);
      applyKeys();
      startSend();
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (!keysHeld.current.delete(key)) return;
      if (keysHeld.current.size === 0) {
        stopSend();
      } else {
        applyKeys();
      }
    };

    const onBlur = () => {
      keysHeld.current.clear();
      stopSend();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [startSend, stopSend]);

  // ── compact canvas ────────────────────────────────────────────────────────
  const compactCanvasRef = useRef<HTMLCanvasElement>(null);
  const compactDragging = useRef(false);

  const drawCompact = useCallback((kx = 0, ky = 0) => {
    const c = compactCanvasRef.current; if (!c) return;
    drawJoy(c, kx, ky, compactDragging.current);
  }, []);
  useEffect(() => { drawCompact(); }, [drawCompact]);

  const onJoyDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (lockedRef.current) return;
    e.stopPropagation(); e.currentTarget.setPointerCapture(e.pointerId);
    compactDragging.current = true;
    const { dx, dy } = clampJoy(compactCanvasRef.current!, e.clientX, e.clientY);
    velRef.current.vx = (-dy / COMPACT_MAX_R) * MAX_LIN;
    velRef.current.vy = (-dx / COMPACT_MAX_R) * MAX_LIN;
    drawCompact(dx, dy); startSend();
  };
  const onJoyMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!compactDragging.current) return;
    const { dx, dy, outside } = clampJoy(compactCanvasRef.current!, e.clientX, e.clientY);
    if (outside) { onJoyUp(); return; }
    velRef.current.vx = (-dy / COMPACT_MAX_R) * MAX_LIN;
    velRef.current.vy = (-dx / COMPACT_MAX_R) * MAX_LIN;
    drawCompact(dx, dy);
  };
  const onJoyUp = () => {
    if (!compactDragging.current) return;
    compactDragging.current = false; velRef.current.vx = 0; velRef.current.vy = 0;
    drawCompact(0, 0); if (!velRef.current.yaw) stopSend();
  };

  const onSliderDown = (e: React.PointerEvent<HTMLInputElement>) => {
    e.stopPropagation(); e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onSliderInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (lockedRef.current) return;
    const yaw = parseFloat(e.currentTarget.value);
    setCompactYawValue(yaw);
    velRef.current.yaw = yaw; startSend();
  };
  const onSliderUp = () => {
    resetYawAfterNativeInput();
    if (!compactDragging.current) stopSend();
  };

  // ── fullscreen canvas ─────────────────────────────────────────────────────
  const fsCanvasRef = useRef<HTMLCanvasElement>(null);
  const fsContainerRef = useRef<HTMLDivElement>(null);
  const fsDragging = useRef(false);

  const drawFS = useCallback((kx = 0, ky = 0) => {
    const c = fsCanvasRef.current; if (!c) return;
    drawJoy(c, kx, ky, fsDragging.current);
  }, []);

  useEffect(() => {
    if (!fsOpen) return;
    const container = fsContainerRef.current;
    const canvas = fsCanvasRef.current;
    if (!container || !canvas) return;
    const resize = () => {
      const size = Math.max(80, Math.floor(Math.min(container.clientWidth, container.clientHeight)) - 20);
      if (canvas.width !== size || canvas.height !== size) {
        canvas.width = size; canvas.height = size; drawFS();
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    const onOrient = () => setTimeout(resize, 150);
    window.addEventListener('orientationchange', onOrient);
    return () => { ro.disconnect(); window.removeEventListener('orientationchange', onOrient); };
  }, [fsOpen, drawFS]);

  const onFsJoyDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (lockedRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    fsDragging.current = true;
    const { dx, dy, maxR } = clampJoy(fsCanvasRef.current!, e.clientX, e.clientY);
    velRef.current.vx = (-dy / maxR) * MAX_LIN; velRef.current.vy = (-dx / maxR) * MAX_LIN;
    drawFS(dx, dy); startSend();
  };
  const onFsJoyMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!fsDragging.current) return;
    const { dx, dy, maxR, outside } = clampJoy(fsCanvasRef.current!, e.clientX, e.clientY);
    if (outside) { onFsJoyUp(); return; }
    velRef.current.vx = (-dy / maxR) * MAX_LIN; velRef.current.vy = (-dx / maxR) * MAX_LIN;
    drawFS(dx, dy);
  };
  const onFsJoyUp = () => {
    if (!fsDragging.current) return;
    fsDragging.current = false; velRef.current.vx = 0; velRef.current.vy = 0;
    drawFS(0, 0); if (!velRef.current.yaw) stopSend();
  };

  const onFsSliderDown = (e: React.PointerEvent<HTMLInputElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onFsSliderInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (lockedRef.current) return;
    const yaw = parseFloat(e.currentTarget.value);
    setFsYawValue(yaw);
    velRef.current.yaw = yaw; startSend();
  };
  const onFsSliderUp = () => {
    resetYawAfterNativeInput();
    if (!fsDragging.current) stopSend();
  };

  // ── fullscreen open/close ─────────────────────────────────────────────────
  const closeFS = useCallback(() => {
    setFsOpen(false);
    fsDragging.current = false;
    if (nativeFsActive.current) {
      nativeFsActive.current = false;
      document.exitFullscreen?.().catch(() => {});
    }
    stopSend();
  }, [stopSend]);

  const openFS = () => {
    setFsOpen(true);
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen()
        .then(() => { nativeFsActive.current = true; })
        .catch(() => {});
    }
  };

  useEffect(() => {
    if (!fsOpen) return;
    const handler = () => {
      if (document.fullscreenElement) return;
      if (!nativeFsActive.current) return;
      nativeFsActive.current = false;
      closeFS();
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [closeFS, fsOpen]);

  // ── mobile canvas ─────────────────────────────────────────────────────────
  const mobileCanvasRef = useRef<HTMLCanvasElement>(null);
  const mobileDragging = useRef(false);
  const mobileMaxR = MOBILE_JOY_SIZE / 2 - 13 - 3;

  const drawMobile = useCallback((kx = 0, ky = 0) => {
    const c = mobileCanvasRef.current; if (!c) return;
    drawJoy(c, kx, ky, mobileDragging.current);
  }, []);
  useEffect(() => { if (variant === 'mobile') drawMobile(); }, [variant, drawMobile]);

  const onMobileJoyDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (lockedRef.current) return;
    e.stopPropagation(); e.currentTarget.setPointerCapture(e.pointerId);
    mobileDragging.current = true;
    const { dx, dy } = clampJoy(mobileCanvasRef.current!, e.clientX, e.clientY);
    velRef.current.vx = (-dy / mobileMaxR) * MAX_LIN;
    velRef.current.vy = (-dx / mobileMaxR) * MAX_LIN;
    drawMobile(dx, dy); startSend();
  };
  const onMobileJoyMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!mobileDragging.current) return;
    // Don't release when the finger leaves the circle — setPointerCapture
    // keeps events flowing to this canvas, and clampJoy already pins dx/dy
    // to the inner ring (max magnitude). Direction follows the finger, so
    // the user can drift their thumb off the visible circle and still drive
    // at full speed in that direction until they lift up.
    const { dx, dy } = clampJoy(mobileCanvasRef.current!, e.clientX, e.clientY);
    velRef.current.vx = (-dy / mobileMaxR) * MAX_LIN;
    velRef.current.vy = (-dx / mobileMaxR) * MAX_LIN;
    drawMobile(dx, dy);
  };
  const onMobileJoyUp = () => {
    if (!mobileDragging.current) return;
    mobileDragging.current = false; velRef.current.vx = 0; velRef.current.vy = 0;
    drawMobile(0, 0); if (!velRef.current.yaw) stopSend();
  };

  const onMobileSliderDown = (e: React.PointerEvent<HTMLInputElement>) => {
    e.stopPropagation(); e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMobileSliderInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (lockedRef.current) return;
    const yaw = parseFloat(e.currentTarget.value);
    setMobileYawValue(yaw);
    velRef.current.yaw = yaw; startSend();
  };
  const onMobileSliderUp = () => {
    resetYawAfterNativeInput();
    if (!mobileDragging.current) stopSend();
  };

  // ── mobile render ─────────────────────────────────────────────────────────
  if (variant === 'mobile') {
    return (
      <>
        <style>{GLOW_STYLE}</style>
        <div className="relative h-full w-full select-none overflow-hidden bg-gradient-to-b from-gray-900 to-black" style={{ touchAction: 'none' }}>
          {/* Top bar — close X (left), acquire/release (right) */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-start justify-between p-3 gap-2">
            {onClose ? (
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-black/55 backdrop-blur-sm text-white border border-white/15"
              >
                <X size={22} />
              </button>
            ) : <div />}

            {otherUserControlling ? (
              <div className="rounded-full bg-black/55 backdrop-blur-sm border border-gray-500 px-4 py-2 text-sm font-semibold text-gray-200">
                {teleopUser} is controlling
              </div>
            ) : (
              <button
                onClick={() => onToggleTeleop(overridden)}
                className={`rounded-full bg-black/55 backdrop-blur-sm border-2 border-orange-400 px-4 py-2 text-sm font-semibold text-orange-300 hover:bg-orange-400/10 transition ${overridden && activeWaypoint ? 'jog-orange-glow' : ''}`}
              >
                {overridden ? 'Release Control' : 'Take Control'}
              </button>
            )}
          </div>

          {/* Bottom row — joystick BL, slider BR */}
          <div className="absolute bottom-0 left-0 right-0 z-10 p-4 flex items-end justify-between gap-3 pointer-events-none">
            <div className="pointer-events-auto">
              <canvas
                ref={mobileCanvasRef}
                width={MOBILE_JOY_SIZE}
                height={MOBILE_JOY_SIZE}
                style={{
                  cursor: 'crosshair',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle,rgba(30,40,80,.92),rgba(15,20,40,.92))',
                  border: '2px solid rgba(96,165,250,0.95)',
                  touchAction: 'none',
                  display: 'block',
                  opacity: locked ? 0.4 : 1,
                  transition: 'opacity 0.2s',
                }}
                onPointerDown={onMobileJoyDown}
                onPointerMove={onMobileJoyMove}
                onPointerUp={onMobileJoyUp}
                onPointerCancel={onMobileJoyUp}
              />
            </div>

            <div
              className="pointer-events-auto flex items-center gap-2 rounded-2xl bg-black/55 backdrop-blur-sm border border-white/15 px-3 py-2"
              style={{ width: 170 }}
            >
              <span className="text-base leading-none text-gray-300">&#8634;</span>
              <input
                ref={mobileSliderRef}
                type="range" min="-1" max="1" step="0.01"
                value={mobileYawValue}
                disabled={locked}
                className="jog-mobile-slider"
                onPointerDown={onMobileSliderDown}
                onChange={onMobileSliderInput}
                onPointerUp={onMobileSliderUp}
                onPointerCancel={onMobileSliderUp}
                onLostPointerCapture={onMobileSliderUp}
                onMouseUp={onMobileSliderUp}
                onTouchEnd={onMobileSliderUp}
              />
              <span className="text-base leading-none text-gray-300">&#8635;</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{GLOW_STYLE}</style>
      <div className="relative flex flex-col gap-2 select-none" onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-400">Manual Control</span>
            <div className="flex items-center gap-2">
              <div
                className="flex items-center gap-1 cursor-default"
                onMouseEnter={() => setShowKb(true)}
                onMouseLeave={() => setShowKb(false)}
              >
                <Info className="h-3 w-3 text-gray-500" />
                <span className="text-[10px] text-gray-500">Keyboard Controls</span>
                {showKb && <div className="absolute top-7 left-0 z-50 w-44 flex flex-col gap-1.5 rounded-xl border border-gray-700 shadow-lg p-3" style={{ background: '#0a0a0f' }}>
                  <span className="text-[10px] font-semibold text-gray-300 mb-0.5">Keyboard Controls</span>
                  {KB_BINDINGS.map(({ keys, action }) => (
                    <div key={action} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        {keys.map((k) => (
                          <kbd key={k} className="inline-flex items-center justify-center rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 font-mono text-[10px] text-gray-300">
                            {k}
                          </kbd>
                        ))}
                      </div>
                      <span className="text-[10px] text-gray-500">{action}</span>
                    </div>
                  ))}
                </div>}
              </div>
              <button
                onClick={openFS}
                className="rounded p-1 text-gray-500 hover:text-gray-300 hover:bg-gray-800/60 transition"
                title="Open fullscreen"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className={`flex flex-col items-center gap-2 transition-opacity ${locked ? 'opacity-35 pointer-events-none' : ''}`}>
            <canvas
              ref={compactCanvasRef} width={COMPACT_SIZE} height={COMPACT_SIZE}
              style={{ cursor: 'crosshair', borderRadius: '50%', background: 'radial-gradient(circle,rgba(30,40,80,.95),rgba(15,20,40,.98))', border: '2px solid rgba(96,165,250,0.95)', touchAction: 'none', display: 'block' }}
              onPointerDown={onJoyDown} onPointerMove={onJoyMove} onPointerUp={onJoyUp} onPointerCancel={onJoyUp}
            />
            <div className="flex w-4/5 min-w-0 items-center gap-2">
              <span className="shrink-0 text-lg leading-none text-gray-400">&#8634;</span>
              <input
                ref={compactSliderRef}
                type="range" min="-1" max="1" step="0.01"
                value={compactYawValue}
                className="min-w-0 flex-1 accent-blue-400"
                style={{ cursor: 'pointer' }}
                onPointerDown={onSliderDown} onChange={onSliderInput}
                onPointerUp={onSliderUp} onPointerCancel={onSliderUp}
                onLostPointerCapture={onSliderUp}
                onMouseUp={onSliderUp}
                onTouchEnd={onSliderUp}
              />
              <span className="shrink-0 text-lg leading-none text-gray-400">&#8635;</span>
            </div>
          </div>
          {otherUserControlling ? (
            <div className="w-full rounded-full border-2 border-gray-600 px-4 py-3 text-center text-base font-semibold text-gray-300 bg-gray-800/50">
              {teleopUser} is controlling
            </div>
          ) : (
            <button
              onClick={() => onToggleTeleop(overridden)}
              className={`w-full rounded-full border-2 border-orange-400 px-4 py-3 text-base font-semibold text-orange-400 hover:bg-orange-400/10 transition ${overridden && activeWaypoint ? 'jog-orange-glow' : ''}`}
            >
              {overridden ? 'Release Control' : 'Take Control'}
            </button>
          )}
      </div>

      {fsOpen && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#0a0a0f', display: 'flex', flexDirection: 'column', userSelect: 'none', touchAction: 'none' }}>
          <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'row', alignItems: 'stretch', padding: 12, gap: 12, boxSizing: 'border-box' }}>
            <div ref={fsContainerRef} style={{ flex: '0 0 28vw', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <canvas
                ref={fsCanvasRef} width={220} height={220}
                style={{ cursor: 'crosshair', borderRadius: '50%', background: 'radial-gradient(circle,rgba(30,40,80,.95),rgba(15,20,40,.98))', border: '2px solid rgba(96,165,250,0.95)', touchAction: 'none', display: 'block', opacity: locked ? 0.35 : 1, transition: 'opacity 0.2s' }}
                onPointerDown={onFsJoyDown} onPointerMove={onFsJoyMove} onPointerUp={onFsJoyUp} onPointerCancel={onFsJoyUp}
              />
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0, borderRadius: 12, overflow: 'hidden', background: '#111118' }}>
              {renderCamera?.()}
            </div>
            <div style={{ flex: '0 0 28vw', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#9ca3af', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 44 }}>Yaw</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minWidth: 0, padding: '0 40px', boxSizing: 'border-box' }}>
                <span style={{ flexShrink: 0, color: '#9ca3af', fontSize: '1.6rem', lineHeight: 1 }}>&#8634;</span>
                <input
                  ref={fsSliderRef}
                  type="range" min="-1" max="1" step="0.01"
                  value={fsYawValue}
                  className={`jog-fs-slider${locked ? ' locked' : ''}`}
                  disabled={locked}
                  onPointerDown={onFsSliderDown} onChange={onFsSliderInput}
                  onPointerUp={onFsSliderUp} onPointerCancel={onFsSliderUp}
                  onLostPointerCapture={onFsSliderUp}
                  onMouseUp={onFsSliderUp}
                  onTouchEnd={onFsSliderUp}
                />
                <span style={{ flexShrink: 0, color: '#9ca3af', fontSize: '1.6rem', lineHeight: 1 }}>&#8635;</span>
              </div>
            </div>
          </div>
          <div style={{ flexShrink: 0, background: '#030712', padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            {otherUserControlling ? (
              <div style={{ fontSize: '0.95rem', padding: '10px 28px', borderRadius: 10, background: 'rgba(31,41,55,0.5)', color: '#d1d5db', fontWeight: 700, border: '1.5px solid #4b5563', minWidth: 140, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {teleopUser} is controlling
              </div>
            ) : (
              <button
                onClick={() => onToggleTeleop(overridden)}
                style={{ fontSize: '0.95rem', padding: '10px 28px', borderRadius: 10, background: 'transparent', color: '#fb923c', fontWeight: 700, border: '1.5px solid rgba(249,115,22,0.6)', cursor: 'pointer', minWidth: 140, minHeight: 48 }}
              >
                {overridden ? 'Release Control' : 'Take Control'}
              </button>
            )}
            <button
              onClick={closeFS}
              style={{ fontSize: '0.95rem', padding: '10px 28px', borderRadius: 10, background: '#374151', color: 'white', fontWeight: 700, border: 'none', cursor: 'pointer', minWidth: 140, minHeight: 48 }}
            >
              &#10005; Exit
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
